import { BasicTable } from "components/tables/Table";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { Row } from "react-table";
import { Grid } from "@mui/material";
import { PaginationState } from "types/common";
import { IStock } from "types/stock";
import api from "api/api";
import serverCall from "api/serverCall";

type Props = {
  equities: IStock[];
  page: PaginationState;
  loading: boolean;
  totalNumberOfEquities: number;
};

function EquitySection({
  page,
  equities,
  totalNumberOfEquities,
  loading,
}: Props) {
	const theme = useTheme();
	const EditableCell = ({
		rowData,
		fieldName,
	}: {
		rowData: IStock;
		fieldName: keyof IStock;
	}) => {
		const [isEditing, setIsEditing] = useState(false);
		const [value, setValue] = useState(rowData?.[fieldName] ?? "");
		const [stockId, setStockId] = useState(rowData.stockId);
		useEffect(() => {
			setStockId(stockId);
		}, []);
		const handleSave = async (stockId: string) => {
			const updatedEquity = equities.map((data: IStock) => {
				if (data.stockId === stockId) {
					return {
						...data,
						[fieldName]: value ?? "",
					};
				}
				return data;
			});
			setIsEditing(false);
			try {
				const response = await serverCall.post<any>(
					api.updateEquityInstruments(),
					updatedEquity
				);
				console.log(response, "REsponse >>>");
			} catch (error) {
				setValue(rowData?.[fieldName] ?? "");
				console.error(error);
			} finally {
				// setEquitiesLoading(false);
			}
		};

		return (
			<div
				onDoubleClick={() => {
					setIsEditing(true);
				}}
				style={{ minHeight: "24px", minWidth: "40px", cursor: "pointer" }}>
				{isEditing ? (
					<input
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onBlur={() => handleSave(stockId)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleSave(stockId);
						}}
						autoFocus
						style={{ width: "100%" }}
					/>
				) : (
					<span>{value}</span>
				)}
			</div>
		);
	};

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        Cell: ({ row }: { row: Row }) => {
          return <Grid sx={{ p: 0.125 }}>{row.index + 1}</Grid>;
        },
      },
      {
        Header: "Stock Id",
        accessor: "stockId",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return <>{equity?.stockId}</>;
        },
      },
      {
        Header: "Stock",
        accessor: "stockName",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return <EditableCell rowData={equity} fieldName={"stockName"} />;
        },
      },
      {
        Header: "Zerodha Trading Symbol",
        accessor: "zerodhaTradingSymbol",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"zerodhaTradingSymbol"} />
          );
        },
      },
      {
        Header: "Zerodha Instrument Token",
        accessor: "zerodhaInstrumentToken",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell
              rowData={equity}
              fieldName={"zerodhaInstrumentToken"}
            />
          );
        },
      },
      {
        Header: "Upstox Trading Symbol",
        accessor: "upstoxTradingSymbol",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"upstoxTradingSymbol"} />
          );
        },
      },
      {
        Header: "Upstox Instrument Key",
        accessor: "upstoxInstrumentKey",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"upstoxInstrumentKey"} />
          );
        },
      },
      {
        Header: "Kotak Trading Symbol",
        accessor: "kotakTradingSymbol",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"kotakTradingSymbol"} />
          );
        },
      },
      {
        Header: "Kotak Instrument Token",
        accessor: "kotakInstrumentToken",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"kotakInstrumentToken"} />
          );
        },
      },
      {
        Header: "Fyers Trading Symbol",
        accessor: "fyersTradingSymbol",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"fyersTradingSymbol"} />
          );
        },
      },
      {
        Header: "Five Paisa Scrip Data",
        accessor: "fivePaisaScripData",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"fivePaisaScripData"} />
          );
        },
      },
      {
        Header: "Angel Trading Symbol",
        accessor: "angelTradingSymbol",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"angelTradingSymbol"} />
          );
        },
      },
      {
        Header: "Angel Symbol Token",
        accessor: "angelSymbolToken",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return (
            <EditableCell rowData={equity} fieldName={"angelSymbolToken"} />
          );
        },
      },
      {
        Header: "ICICI Stock Code",
        accessor: "iciciStockCode",
        Cell: ({ row }: { row: Row }) => {
          const equity = row.original as IStock;
          return <EditableCell rowData={equity} fieldName={"iciciStockCode"} />;
        },
      },
    ],
    [theme, page, equities]
  );

  return (
    <>
      <BasicTable
        columns={columns}
        data={equities ?? []}
        title={`Equities:(${totalNumberOfEquities})`}
        isLoading={loading}
        loadingText="Loading..."
        emptyMessage="No equities found...!"
      />
    </>
  );
}

export default EquitySection;
